const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/modules/CIS4m/components/CIS4mTextField.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { TextField, TextFieldProps, useTheme } from '@mui/material';
import { useCIS4mValidation } from '../hooks';
import { CIS4mValueComponentProps } from '../types';
import textMap from './translations';
import { ValidationFunction } from '../../../components/Forms/utils/validation';
import { TextInput, useOptionalText } from '../../../utils/intlUtils';
import { useCIS4mContext } from '../context';

export interface CIS4mTextFieldProps extends CIS4mValueComponentProps {
    label?: TextInput;
    required?: boolean;
    className?: string;
    helperText?: TextInput;
    checkValidity?: ValidationFunction;
    mask?: string;
    maxLength?: number;
    multiline?: boolean;
    color?: 'inversed';
}

const sxTextField: TextFieldProps['sx'] = {
    mt: 0.5,
    mb: 0.5,
};

export const CIS4mTextField = forwardRef((props: CIS4mTextFieldProps, ref: any) => {
    const {
        checkValidity,
        className,
        disabled = false,
        hidden = false,
        initialValue,
        mask,
        multiline = false,
        path,
        readonly = false,
        required = false,
    } = props;
    const { variant } = useCIS4mContext().style;

    const theme = useTheme();
    const label = useOptionalText(props.label);
    const helperText = useOptionalText(props.helperText);

    const [value, setValue, errorMessage] = useCIS4mValidation(ref, {
        checkValidity: checkValidity
            ? checkValidity
            : () => {
                  if (
                      mask !== undefined &&
                      mask.trim().length !== 0 &&
                      value.replaceAll('_', '').length !== mask.length
                  ) {
                      return textMap.formatDoesNotMatch;
                  }
              },
        disabled,
        hidden,
        initialValue,
        path,
        required,
    });

    return hidden ? null : (
        <TextField
            label={label}
            className={className}
            variant={variant}
            fullWidth
            multiline={multiline}
            size={'small'}
            value={value || ''}
            onChange={(event: any) => {
                setValue(event.target.value);
            }}
            slotProps={{
                input: {
                    readOnly: readonly,
                    inputProps: { maxLength: props.maxLength },
                    style: { color: props.color === 'inversed' ? theme.palette.common.white : undefined },
                },
                inputLabel: {
                    style: { color: props.color === 'inversed' ? theme.palette.common.white : undefined },
                },
            }}
            color={props.color === 'inversed' ? undefined : !!errorMessage ? 'error' : 'primary'}
            required={required}
            disabled={disabled}
            hidden={hidden}
            error={!!errorMessage}
            helperText={errorMessage || helperText} // errorMessage will have more priority than helperText when there is one
            sx={sxTextField}
        />
    );
});
CIS4mTextField.displayName = 'CIS4mTextField';
